import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import QualificationWizard from "../components/QualificationWizard";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Deal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [ndaChecked, setNdaChecked] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [showWelcomeNotification, setShowWelcomeNotification] = useState(false);

  useEffect(() => {
    document.title = "Jigsaw Deal Room – Buyer Qualification";
    init();
    const hasSeenWelcome = localStorage.getItem('dealroom_welcome_seen');
    if (!hasSeenWelcome) {
      setTimeout(() => setShowWelcomeNotification(true), 1000);
    }
  }, []);

  const init = async () => {
    try {
      const authResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      if (authResponse.data.ok) {
        setUser(authResponse.data.user);
      }
      await loadSubmission();
    } catch (err) {
      console.error("Initialization failed:", err);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const loadSubmission = async () => {
    try {
      const response = await axios.get(`${API}/submission/me`, {
        withCredentials: true,
      });
      if (response.data.ok) {
        setSubmission(response.data.submission);
      }
    } catch (err) {
      console.error("Failed to load submission:", err);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const handleNdaAccept = async () => {
    if (!ndaChecked) {
      alert("Please check the box to confirm your understanding.");
      return;
    }

    try {
      const response = await axios.patch(
        `${API}/submission/me/nda`,
        { nda_ack: true },
        { withCredentials: true }
      );

      if (response.data.ok) {
        await loadSubmission();
      }
    } catch (err) {
      console.error("NDA update error:", err);
      alert("Failed to update NDA acknowledgment. Please try again.");
    }
  };

  const dismissWelcomeNotification = () => {
    setShowWelcomeNotification(false);
    localStorage.setItem('dealroom_welcome_seen', 'true');
  };

  const handleFinalSubmit = async () => {
    if (!window.confirm("Are you ready to submit your qualification for review? This cannot be undone.")) {
      return;
    }

    setSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await axios.post(
        `${API}/submission/me/submit`,
        {},
        { withCredentials: true }
      );

      if (response.data.ok) {
        setSubmitResult({
          success: true,
          score: response.data.score,
          score_band: response.data.score_band
        });
        await loadSubmission();
        alert(`✅ Submission successful!\n\nYour qualification score: ${response.data.score}/100\nCategory: ${response.data.score_band.toUpperCase()}\n\nCheck your email for confirmation. Our team will be in touch shortly.`);
      }
    } catch (err) {
      console.error("Submission error:", err);
      const errors = err.response?.data?.detail?.errors || ["An error occurred during submission"];
      setSubmitResult({
        success: false,
        errors
      });
      alert(`❌ Submission failed:\n\n${errors.join('\n')}\n\nPlease complete all required fields and try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  const ndaAck = submission?.nda_ack || false;
  const isSubmitted = submission?.status === "submitted";

  return (
    <div className="min-h-screen text-white flex flex-col relative">
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/z6ik3546_Subtle%20Pattern%20On%20Blue.png)',
        }}
      />
      
      {/* Side Fade Overlays */}
      <div className="fixed inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60 pointer-events-none" />
      
      {/* Mobile-Optimized Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900/50 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title - Compact on mobile */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <a 
                href="https://www.peakassetinvestments.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity flex-shrink-0"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/ff1ei5kf_RGB_Motif%20Small%20Full%20Colour%20White.png"
                  alt="Peak Asset Investments"
                  className="h-6 sm:h-8 w-auto"
                />
              </a>
              <div className="hidden sm:block h-6 w-px bg-white/20 mx-2" />
              <div className="min-w-0">
                <div className="text-xs sm:text-sm font-semibold truncate">Jigsaw Deal Room</div>
                <div className="text-[10px] sm:text-xs text-slate-400 truncate hidden sm:block">Buyer Qualification</div>
              </div>
            </div>
            
            {/* User & Logout - Compact on mobile */}
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="text-[10px] sm:text-xs text-slate-400 truncate max-w-[100px] sm:max-w-none hidden xs:block">
                {user?.email}
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-[10px] sm:text-xs text-slate-300 hover:text-white transition px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-white/10 hover:border-white/20 whitespace-nowrap"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Welcome Notification - Mobile Optimized */}
      {showWelcomeNotification && (
        <div className="relative z-20 animate-slide-down">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-cyan-500/10 backdrop-blur-xl border border-cyan-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-transparent to-blue-500/5 animate-pulse" />
              
              <div className="relative">
                {/* Close button - Top right on mobile */}
                <button
                  onClick={dismissWelcomeNotification}
                  className="absolute -top-1 -right-1 sm:top-0 sm:right-0 text-slate-400 hover:text-white transition-colors p-1.5 sm:p-2 rounded-lg hover:bg-white/5"
                  aria-label="Dismiss notification"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Content - Stacked on mobile */}
                <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 pr-6 sm:pr-8">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <span className="text-xl sm:text-2xl">👋</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-1.5 sm:mb-2">
                      Welcome to Your Deal Room
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mb-2 sm:mb-3">
                      Your progress is automatically saved as you work. Feel free to take breaks or switch devices — your data is always protected.
                    </p>
                    <div className="flex items-center gap-2 text-[10px] sm:text-xs text-cyan-300">
                      <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">Auto-save enabled</span>
                    </div>
                  </div>
                </div>

                <div className="relative mt-3 pt-2 sm:pt-3 border-t border-cyan-500/20">
                  <p className="text-[10px] sm:text-xs text-slate-400 italic">
                    Tap × to dismiss
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Mobile First */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12 flex-1">
        {/* Page Title */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2">Your Deal Room</h1>
          <p className="text-sm sm:text-base text-slate-400">Complete these steps to proceed</p>
        </div>

        {/* Steps Grid - Stack on mobile, 3-col on desktop */}
        <div className="space-y-4 sm:space-y-6 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
          
          {/* Step 1: NDA */}
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-sm sm:text-base">
                1
              </div>
              <h2 className="text-lg sm:text-xl font-bold">NDA & Principles</h2>
              {ndaAck && (
                <span className="ml-auto text-green-400 text-lg sm:text-xl">✓</span>
              )}
            </div>

            {ndaAck ? (
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                <div className="text-xs sm:text-sm font-medium text-green-300">
                  Acknowledged {new Date(submission.nda_ack_at).toLocaleDateString()}
                </div>
              </div>
            ) : (
              <div className="flex flex-col flex-1">
                <div className="bg-slate-800/30 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 text-[11px] sm:text-xs text-slate-300 space-y-2 overflow-auto max-h-32 sm:max-h-48">
                  <p className="font-semibold text-white text-xs sm:text-sm">Deal Room Principles:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>All information is confidential</li>
                    <li>No sharing without written consent</li>
                    <li>Engage in good faith with accurate info</li>
                  </ul>
                </div>
                <label className="flex items-start gap-2 cursor-pointer mb-3 sm:mb-4">
                  <input
                    type="checkbox"
                    checked={ndaChecked}
                    onChange={(e) => setNdaChecked(e.target.checked)}
                    className="mt-0.5 sm:mt-1 w-4 h-4"
                  />
                  <span className="text-[11px] sm:text-xs text-slate-300">
                    I acknowledge and agree <span className="text-red-400">*</span>
                  </span>
                </label>
                <button
                  onClick={handleNdaAccept}
                  disabled={!ndaChecked}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-2.5 sm:py-2 px-4 rounded-lg sm:rounded-xl transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  I Acknowledge
                </button>
              </div>
            )}
          </div>

          {/* Step 2: Wizard */}
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-sm sm:text-base">
                2
              </div>
              <h2 className="text-lg sm:text-xl font-bold">Qualification</h2>
            </div>

            <p className="text-xs sm:text-sm text-slate-300 mb-3 sm:mb-4">
              7-step process to understand your investment profile.
            </p>

            {/* Steps list - More compact on mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-1 gap-1.5 sm:gap-2 text-[10px] sm:text-xs mb-4 sm:mb-6 flex-1">
              {[
                "Investor Roster",
                "Proof of Funds", 
                "Track Record",
                "Thesis & Fit",
                "Operator Plans",
                "Structure",
                "Schedule"
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-1.5 sm:gap-2 text-slate-400">
                  <span className="text-cyan-400 font-medium">{i + 1}</span>
                  <span className="truncate">{step}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowWizard(true)}
              disabled={!ndaAck}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 sm:py-2 px-4 rounded-lg sm:rounded-xl transition text-sm disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
            >
              {submission?.wizard ? "Continue" : "Start"} Wizard
            </button>
          </div>

          {/* Step 3: Submit */}
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 flex flex-col">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl bg-green-500/20 flex items-center justify-center text-green-400 font-bold text-sm sm:text-base">
                3
              </div>
              <h2 className="text-lg sm:text-xl font-bold">Submit</h2>
              {isSubmitted && (
                <span className="ml-auto text-green-400 text-lg sm:text-xl">✓</span>
              )}
            </div>

            <p className="text-xs sm:text-sm text-slate-300 mb-3 sm:mb-4 flex-1">
              Review and submit for Jigsaw team review.
            </p>

            {submission?.wizard?.slot && (
              <div className="bg-slate-800/30 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
                <div className="text-[10px] sm:text-xs text-slate-400 mb-0.5">Selected Slot:</div>
                <div className="text-xs sm:text-sm font-medium text-white">
                  {new Date(submission.wizard.slot).toLocaleString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </div>
              </div>
            )}

            <button
              onClick={handleFinalSubmit}
              disabled={!ndaAck || !submission?.wizard || submitting || isSubmitted}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 sm:py-2 px-4 rounded-lg sm:rounded-xl transition text-sm disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
            >
              {submitting ? "Submitting..." : isSubmitted ? "Submitted ✓" : "Submit for Review"}
            </button>
          </div>
        </div>

        {/* Help Box - Compact on mobile */}
        <div className="mt-6 sm:mt-8 bg-blue-900/20 border border-blue-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="text-blue-400 text-lg sm:text-xl flex-shrink-0">ℹ️</div>
            <div className="min-w-0">
              <h3 className="text-xs sm:text-sm font-semibold mb-0.5 sm:mb-1">Need Help?</h3>
              <p className="text-[11px] sm:text-xs text-slate-300">
                Contact{" "}
                <a href="mailto:info@peakassetinvestments.com" className="text-cyan-400 hover:text-cyan-300 underline break-all">
                  info@peakassetinvestments.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Wizard Modal */}
      {showWizard && (
        <QualificationWizard
          onClose={() => {
            setShowWizard(false);
            loadSubmission();
          }}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Deal;
